import { DataServiceType, TileJson } from './types';
import { Info360TileMetadata, Info360TileMetadataV2 } from './Info360TileTypes';
import { LngLatBounds, LngLatLike } from 'mapbox-gl';
import { convertCoordsToBounds, notUndefined } from '@Map/utils';

import CancellableFetch from './CancellableFetch';
import Info360TileServiceV2 from './Info360TileServiceV2';
import { SERVICE_ERROR } from './constants';
import logger from '../logger/Logger';

interface AuthResponse {
	authenticated: boolean;
	cookiesSet: boolean;
	expiresAt: string;
}

const AUTH_RETRY_MAX = 3;
const AUTH_RETRY_DELAYS = [1000, 2000, 4000]; // exponential backoff: 1s, 2s, 4s
const AUTH_REFRESH_BUFFER_MS = 30 * 60 * 1000; // 30 minutes before expiry

export default class Info360TileServiceV3 extends Info360TileServiceV2 {
	private _authTimeout: NodeJS.Timeout | undefined;
	private _authRequest: CancellableFetch | undefined;
	private _authRetryCount: number = 0;
	private _isAuthenticating: boolean = false;

	constructor(dataService: DataServiceType<Info360TileMetadataV2>) {
		super(dataService);
	}

	/**
	 * Override dataEndpoint to use v3 tileset-index
	 */
	get dataEndpoint(): string | null {
		return `${this.endpoint}/v3/tileset-index`;
	}

	/**
	 * Get auth endpoint URL
	 */
	get authEndpoint(): string | null {
		return `${this.endpoint}/v3/auth`;
	}

	/**
	 * Authenticate with v3 auth endpoint to set cookies
	 * Returns the expiration time for scheduling the next refresh
	 */
	private async authenticate(): Promise<Date | null> {
		const authEndpoint = this.authEndpoint;
		if (!authEndpoint) {
			logger.error('Auth endpoint is null, cannot authenticate');
			return null;
		}

		this._isAuthenticating = true;

		try {
			this._authRequest = new CancellableFetch({
				url: authEndpoint,
				headers: this._requestHeaders,
				credentials: this._credentials,
				retries: AUTH_RETRY_MAX,
				retryDelay: AUTH_RETRY_DELAYS[this._authRetryCount] || 1000,
			});

			const response = await this._authRequest.getPage<AuthResponse>({
				responseParser: data => data as AuthResponse,
			});

			if (!response || !response.authenticated || !response.cookiesSet) {
				throw new Error(
					'Authentication failed: cookies not set or not authenticated',
				);
			}

			logger.info('V3 authentication successful', {
				expiresAt: response.expiresAt,
			});

			this._authRetryCount = 0; // Reset retry count on success
			return new Date(response.expiresAt);
		} catch (error) {
			this._authRetryCount++;
			logger.error('V3 authentication failed', {
				error,
				retryCount: this._authRetryCount,
				maxRetries: AUTH_RETRY_MAX,
			});

			if (this._authRetryCount < AUTH_RETRY_MAX) {
				// Retry authentication
				const delay =
					AUTH_RETRY_DELAYS[this._authRetryCount - 1] || 1000;
				logger.info(`Retrying authentication in ${delay}ms`);
				await new Promise(resolve => setTimeout(resolve, delay));
				return this.authenticate();
			}

			throw error;
		} finally {
			this._isAuthenticating = false;
			this._authRequest = undefined;
		}
	}

	/**
	 * Schedule next authentication refresh based on cookie expiration
	 */
	private scheduleAuthRefresh(expiresAt: Date): void {
		// Clear any existing timeout
		this.stopAuthRefresh();

		// Calculate when to refresh (30 minutes before expiry)
		const refreshAt = new Date(
			expiresAt.getTime() - AUTH_REFRESH_BUFFER_MS,
		);
		const now = new Date();
		const delay = refreshAt.getTime() - now.getTime();

		if (delay <= 0) {
			// Already past refresh time, authenticate immediately
			logger.warn(
				'Cookie expiry is too soon, authenticating immediately',
				{
					expiresAt: expiresAt.toISOString(),
					refreshAt: refreshAt.toISOString(),
				},
			);
			this.authenticate()
				.then(newExpiresAt => {
					if (newExpiresAt) {
						this.scheduleAuthRefresh(newExpiresAt);
					}
				})
				.catch(error => {
					logger.error(
						'Failed to refresh authentication on immediate retry',
						error,
					);
				});
			return;
		}

		logger.info('Scheduling authentication refresh', {
			expiresAt: expiresAt.toISOString(),
			refreshAt: refreshAt.toISOString(),
			delayMs: delay,
		});

		this._authTimeout = setTimeout(() => {
			this.authenticate()
				.then(newExpiresAt => {
					if (newExpiresAt) {
						this.scheduleAuthRefresh(newExpiresAt);
					}
				})
				.catch(error => {
					logger.error('Failed to refresh authentication', error);
				});
		}, delay);
	}

	/**
	 * Stop authentication refresh
	 */
	private stopAuthRefresh(): void {
		if (this._authTimeout) {
			clearTimeout(this._authTimeout);
			this._authTimeout = undefined;
		}
	}

	/**
	 * Cancel any pending auth requests
	 */
	private cancelPendingAuthRequests(): void {
		if (this._authRequest) {
			this._authRequest.cancel();
			this._authRequest = undefined;
		}
	}

	/**
	 * Override createDataSources to only handle TileJson format
	 * V3 does not support LayerV2[] arrays
	 */
	createDataSources(data: Info360TileMetadataV2): void {
		if (!data.systems) return;

		const dataSources = Object.entries(data.systems).flatMap(
			([systemType, entry]) => {
				// V3 only supports TileJson format
				if (!this.isTileJson(entry)) {
					logger.warn(
						`V3 endpoint only supports TileJson format. Skipping non-TileJson entry for system: ${systemType}`,
					);
					return [];
				}
				logger.info(`V3: Processing ${systemType} from tileset-index`);
				const mapped = this._mapTileJson(entry as TileJson, systemType);
				return mapped;
			},
		);

		// Calculate bounds from metadata or TileJson
		if (data?.bounds?.southWest && data?.bounds?.northEast) {
			this._bounds = new LngLatBounds(
				data.bounds.southWest,
				data.bounds.northEast,
			);
		} else {
			const coords = Object.values(data.systems)
				.flatMap(entry => {
					if (this.isTileJson(entry)) {
						const [swLng, swLat, neLng, neLat] = (entry as TileJson)
							.bounds;
						return [
							[swLng, swLat],
							[neLng, neLat],
						];
					}
					return undefined;
				})
				.filter(notUndefined) as LngLatLike[];
			this._bounds = convertCoordsToBounds(coords);
		}

		this._dataSources = dataSources;
	}

	/**
	 * Override loadData to ensure auth happens first
	 */
	async loadData(reload = false): Promise<void> {
		// If already authenticating, wait
		if (this._isAuthenticating) {
			logger.info('Authentication in progress, waiting...');
			// Wait for authentication to complete
			while (this._isAuthenticating) {
				await new Promise(resolve => setTimeout(resolve, 100));
			}
		}

		// Authenticate before loading data
		try {
			const expiresAt = await this.authenticate();
			if (!expiresAt) {
				throw new Error('Authentication failed: no expiry date');
			}

			// Schedule refresh
			this.scheduleAuthRefresh(expiresAt);

			// Now load data using parent implementation
			await super.loadData(reload);
		} catch (error) {
			logger.error('V3 authentication failed, cannot load tiles', {
				error,
			});
			this.fire(SERVICE_ERROR, {
				service: this.id,
				message: 'Failed to authenticate with V3 tiles API',
				error,
			});
			throw error;
		}
	}

	/**
	 * Override parseResponse - V3 response format is identical to V2
	 */
	parseResponse(data: Info360TileMetadataV2): Info360TileMetadata | null {
		return super.parseResponse(data);
	}

	/**
	 * Override update to handle credential changes
	 */
	update(
		dataService: DataServiceType<
			Info360TileMetadata & Info360TileMetadataV2
		>,
	): boolean {
		// Cancel any pending auth requests
		this.cancelPendingAuthRequests();

		// Stop auth refresh timer
		this.stopAuthRefresh();

		// Call parent update
		const result = super.update(dataService);

		// If credentials changed, re-authenticate on next load
		this._authRetryCount = 0;

		return result;
	}

	/**
	 * Override delete to clean up auth resources
	 */
	delete(): void {
		// Stop auth refresh timer
		this.stopAuthRefresh();

		// Cancel any pending auth requests
		this.cancelPendingAuthRequests();

		// Call parent delete
		super.delete();
	}
}
